import { paymentProcessingApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import { payrollReviewGateway } from '../payroll-review';
import { PayrollRunStatus } from '../payroll-review/types';
import type { CheckBatchGateway } from './CheckBatchGateway';
import type { ApprovedPayRun, CheckBatch } from './types';

export class HttpCheckBatchGateway implements CheckBatchGateway {
  private readonly request = createJsonClient(paymentProcessingApiBaseUrl);

  async listApprovedRuns(signal?: AbortSignal): Promise<ApprovedPayRun[]> {
    // The Payroll Review API has no status filter: filtering is the caller's job (see PayrollReviewGateway).
    const runs = await payrollReviewGateway.listRuns(signal);
    return runs
      .filter((r) => r.status === PayrollRunStatus.Approved)
      .map((r) => ({ id: r.id, periodStartDate: r.periodStartDate, periodEndDate: r.periodEndDate, payDate: r.checkDate }))
      .sort((a, b) => b.payDate.localeCompare(a.payDate));
  }

  createBatch(payrollRunId: string): Promise<CheckBatch> {
    return this.request('/api/check-batches', { method: 'POST', body: JSON.stringify({ payrollRunId }) });
  }

  listBatches(payrollRunId: string, signal?: AbortSignal): Promise<CheckBatch[]> {
    return this.request(`/api/check-batches?payrollRunId=${encodeURIComponent(payrollRunId)}`, undefined, signal);
  }

  getBatch(id: string, signal?: AbortSignal): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}`, undefined, signal);
  }

  editCheckNumber(id: string, lineId: string, checkNumber: string, expectedVersion: number): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/check-number`, {
      method: 'PUT',
      body: JSON.stringify({ checkNumber, expectedVersion }),
    });
  }

  removeLines(id: string, lineIds: string[], expectedVersion: number): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/lines/remove`, {
      method: 'POST',
      body: JSON.stringify({ lineIds, expectedVersion }),
    });
  }

  runReadiness(id: string, expectedVersion: number): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/readiness`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
  }

  acknowledgeWarnings(id: string, warningIds: string[], expectedVersion: number): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/warning-acknowledgement`, {
      method: 'POST',
      body: JSON.stringify({ warningIds, expectedVersion }),
    });
  }

  saveDraft(id: string, currentStep: number, expectedVersion: number): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/draft`, {
      method: 'POST',
      body: JSON.stringify({ currentStep, expectedVersion }),
    });
  }

  async discard(id: string, expectedVersion: number): Promise<void> {
    await this.request<unknown>(`/api/check-batches/${encodeURIComponent(id)}/discard`, { method: 'POST', body: JSON.stringify({ expectedVersion }) });
  }

  approve(id: string, expectedVersion: number, idempotencyKey: string): Promise<CheckBatch> {
    return this.request(`/api/check-batches/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ reviewConfirmed: true, expectedVersion }),
    });
  }
}
